/* eslint-disable import/prefer-default-export */
import { concat } from 'ramda';
import { generateListTags, generateTag } from 'utils/helpers/fetch';
import dispatchingApi from '../../dispatchingApi';

// example mock
/*
queryFn: ({ params }) =>
  import('./mockData/viewTrailerDetailsByLocation').then(({ default: getTrailers }) => ({
    data: getTrailers(params),
  })),
*/

const getUrl = concat('compliance/trailer-status/');

const trailerStatusEndpoints = dispatchingApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    addTrailerToLocation: mutation({
      invalidatesTags: (result, error, { trailerId, locationId }) => [
        { id: trailerId, type: 'Trailer' },
        { id: locationId, type: 'Location' },
      ],
      query: ({ trailerId, locationId }) => ({
        body: { locationId, trailerId },
        method: 'POST',
        url: getUrl('add-location-to-trailer'),
      }),
    }),

    createTrailer: mutation({
      invalidatesTags: (result, error, { locationId, trailerId }) => [
        { id: locationId, type: 'Location' },
        { id: trailerId, type: 'Trailer' },
      ],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('create-trailer'),
      }),
    }),

    deleteTrailer: mutation({
      invalidatesTags: (result, error, { locationId, trailerId }) => [
        { id: locationId, type: 'Location' },
        { id: trailerId, type: 'Trailer' },
      ],
      query: ({ trailerId }) => ({
        body: { trailerId },
        method: 'POST',
        url: getUrl('delete-trailer'),
      }),
    }),

    editTrailer: mutation({
      invalidatesTags: (result, error, { locationId, trailerId }) => [
        { id: locationId, type: 'Location' },
        { id: trailerId, type: 'Trailer' },
      ],
      query: ({ trailer }) => {
        return {
          body: trailer,
          method: 'POST',
          url: getUrl('edit-trailer'),
        };
      },
    }),

    fileUpload: mutation({
      query: ({ type, file }) => {
        const body = new FormData();
        body.append('File', file);
        body.append('Type', type);
        return {
          body,
          method: 'POST',
          url: getUrl('file-upload'),
        };
      },
    }),

    getNewLocationsForTrailer: query({
      providesTags: generateListTags('Location', 'locationId'),
      query: ({ params }) => ({
        params,
        url: getUrl(`get-new-locations-for-trailer`),
      }),
      refetchOnMountOrArgChange: true,
    }),

    getNewTrailersForLocation: query({
      providesTags: generateListTags('Trailer', 'trailerId'),
      query: ({ params }) => ({
        params,
        url: getUrl(`get-new-trailers-for-location`),
      }),
      refetchOnMountOrArgChange: true,
    }),

    removeTrailer: mutation({
      invalidatesTags: (result, error, { locationId, trailerId }) => [
        { id: trailerId, type: 'Trailer' },
        { id: locationId, type: 'Location' },
      ],
      query: ({ locationId, trailerId }) => ({
        body: { locationId, trailerId },
        method: 'POST',
        url: getUrl('location-remove-trailer'),
      }),
    }),

    viewLocationDetailsByTrailer: query({
      providesTags: (result, error, { params }) => [
        ...generateListTags('Location', 'locationId', result),
        { id: params.trailerId, type: 'Trailer' },
      ],
      query: ({ params }) => ({
        params,
        url: getUrl('view-details-by-trailer'),
      }),
    }),

    viewTrailer: query({
      providesTags: generateTag('Trailer', 'trailerId'),
      query: params => ({
        params,
        url: getUrl('view-trailer'),
      }),
    }),

    viewTrailerDetailsByLocation: query({
      providesTags: (result, error, { params }) => [
        ...generateListTags('Trailer', 'trailerId', result),
        { id: params.locationId, type: 'Location' },
      ],
      query: ({ params }) => ({
        params,
        url: getUrl('view-details-by-location'),
      }),
    }),

    viewTrailerSummaryByLocation: query({
      providesTags: generateListTags('Location', 'locationId'),
      query: () => ({
        url: getUrl('view-summary-by-location'),
      }),
    }),

    viewTrailerSummaryByTrailer: query({
      providesTags: generateListTags('Trailer', 'trailerId'),
      query: () => ({
        url: getUrl('view-summary-by-trailer'),
      }),
    }),
  }),
});

export const {
  useAddTrailerToLocationMutation,
  useCreateTrailerMutation,
  useDeleteTrailerMutation,
  useEditTrailerMutation,
  useGetNewLocationsForTrailerQuery,
  useGetNewTrailersForLocationQuery,
  useFileUploadMutation,
  useRemoveTrailerMutation,
  useViewTrailerQuery,
  useViewTrailerDetailsByLocationQuery,
  useViewLocationDetailsByTrailerQuery,
  useViewTrailerSummaryByLocationQuery,
  useViewTrailerSummaryByTrailerQuery,
} = trailerStatusEndpoints;
